import React from 'react';
import {Card, Tag, Tooltip} from 'antd';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '../../utils/ROUTES';
import {capitalizeFirstLetter} from '../../utils/TextAlterations';
import {daysAgo} from '../../utils/DateFunctions';
import {formatCurrency} from '../../utils/currency';
import {WorkOutlineOutlined, AttachMoney, LocationOn} from '@mui/icons-material';
import {ScrollShadow} from '@heroui/react';
import {InfoCircleFilled, InfoCircleOutlined} from '@ant-design/icons';

interface RecommendedJobsProps {
	jobs: any[];
	title?: string;
}

const RecommendedJobs: React.FC<RecommendedJobsProps> = ({jobs, title = 'Similar jobs'}) => {
	const navigate = useNavigate();

	if (!jobs || jobs.length === 0) {
		return null;
	}

	return (
		<div className="h-full md:max-w-[260px]">
			{title && (
				<h3 className="text-sm font-normal text-darkCharcoal mb-2 flex items-center gap-2">
					{title}
					<Tooltip title="These jobs are similar to the one you are viewing. You can apply to any of them.">
						<InfoCircleFilled className="text-sm text-antBlue" />
					</Tooltip>
				</h3>
			)}
			<ScrollShadow
				className="max-h-[calc(100dvh-10rem)] h-full overflow-y-auto pr-1"
				style={{
					scrollbarWidth: 'thin',
					scrollbarColor: '#d1d5db transparent',
				}}>
				<div className="flex flex-col gap-2">
					{jobs.map((job) => (
						<Card
							size="small"
							className="h-full transition-all duration-300 hover:border-permBlue cursor-pointer shadow-none border-gray-100"
							bordered={false}
							key={job.id}
							onClick={() => navigate(ROUTES.JOB_PAGE.replace(':jobId', job.id))}>
							<div className="flex flex-col h-full">
								{/* Company and logo */}
								<div className="flex items-center gap-2 mb-2">
									<div className="rounded-full border border-gray-200 p-1 flex-shrink-0">
										{!job?.created_by_image ? (
											<WorkOutlineOutlined
												style={{fontSize: '16px', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
											/>
										) : (
											<img src={job.created_by_image} alt="company logo" className="w-5 h-5 object-cover rounded-full" />
										)}
									</div>
									<div className="truncate text-xs font-medium text-charcoal">{job?.company_name && capitalizeFirstLetter(job.company_name)}</div>
								</div>

								{/* Job title */}
								<h4 className="text-sm font-semibold mb-2 text-darkCharcoal line-clamp-2">{capitalizeFirstLetter(job.title)}</h4>

								{/* Job type tags */}
								<div className="flex flex-wrap gap-1 mb-2">
									{job.engagement_type && <Tag className="text-xs m-0 px-1 py-0">{job.engagement_type}</Tag>}
									{job.job_type && <Tag className="text-xs m-0 px-1 py-0">{job.job_type}</Tag>}
								</div>

								{/* Salary and location */}
								<div className="mt-auto pt-2 border-t border-gray-100">
									{job?.payment_rate_from && job?.show_salary_on_job && (
										<div className="flex items-center gap-1 text-xs text-charcoal mb-1">
											<AttachMoney style={{fontSize: '14px', opacity: 0.7}} />
											<span className="truncate">{formatCurrency(job?.payment_rate_from, job?.payment_rate_to, job?.payment_rate_type, job?.payment_rate_currency)}</span>
										</div>
									)}

									{job.country && (
										<div className="flex items-center gap-1 text-xs text-charcoal">
											<LocationOn style={{fontSize: '14px', opacity: 0.7}} />
											<span className="truncate">{job.country}</span>
										</div>
									)}

									{/* Posted time */}
									{/* <div className="text-xs text-grey mt-1">{job.created_at !== null ? `${daysAgo(job.created_at)}d ago` : ''}</div> */}
								</div>
							</div>
						</Card>
					))}
				</div>
			</ScrollShadow>
		</div>
	);
};

export default RecommendedJobs;
